import { observable, makeObservable, action } from "mobx";
import { ChangeEvent } from "react";
import { RootStore } from "src/stores/RootStore";
import { IAiDetectWithFileResponse, IInvoiceFile, IQRExtractInvoiceData, IRealestate } from "./InvoiceTypes";
import { v4 as uuidv4 } from "uuid";
import { INVOICE_FILE_STATE, INVOICE_FILE_TYPE, INVOICE_PROPERTY } from "./InvoiceEnums";
import { Ticket } from "src/stores/TicketStore";
import i18next from "i18next";
import { MessageType } from "src/components/notifications/Notifier";
import { canAiApplyCreditor } from "./Utils";

export interface FileHTMLInputEvent extends ChangeEvent {
    target: HTMLInputElement & EventTarget;
}

const dataTypes = ["application/pdf"];

export class InvoiceFilePreviewStore {
    rootStore: RootStore;

    isDraggingActive: boolean = false;

    isDraggingOverMainWindow: boolean = false;

    isLoadingPDFFile: boolean = false;
    isFileDeleting: boolean = false;
    isAiPending: boolean = false;
    // User can dismiss the "KI liest Rechnungsdaten aus…" modal while AI is still
    // running; the request keeps going in the background but the modal stays hidden
    // until the next upload resets the flag.
    isAiExtractionModalDismissed: boolean = false;

    currentFileInputEvent: FileHTMLInputEvent | undefined = undefined;
    displayOverrideExistingDataModal: boolean = false;

    // AbortController for the active /ai-detect-with-file (and its parallel QR) call.
    // Not observable — it's transient infrastructure that should never trigger re-renders.
    private currentAbortController?: AbortController;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            isDraggingActive: observable,
            currentFileInputEvent: observable,
            displayOverrideExistingDataModal: observable,
            isLoadingPDFFile: observable,
            isFileDeleting: observable,
            isDraggingOverMainWindow: observable,
            isAiPending: observable,
            isAiExtractionModalDismissed: observable,

            handleFileInputChange: action,
            extractQrCodeDataAndUpdateInvoiceStore: action,
            setCurrentFileInputEvent: action,
            setDisplayOverrideExistingDataModal: action,
            setIsLoadingPDFFile: action,
            setIsFileDeleting: action,
            setIsDraggingOverMainWindow: action,
            setIsDraggingActive: action,
            setIsAiPending: action,
            setIsAiExtractionModalDismissed: action,
            toggleDisplayOverrideExistingDataModal: action,
            handleClickOverrideExistingDataYes: action,
            handleClickOverrideExistingDataNo: action,
            handleDeleteInvoiceFile: action,
            abortAiDetection: action,
            applyAiResult: action,
            downloadFile: action
        });
    }

    setIsDraggingActive = (dragging: boolean) => {
        this.isDraggingActive = dragging;
    };

    setCurrentFileInputEvent = (event: FileHTMLInputEvent) => {
        this.currentFileInputEvent = event;
    };

    setDisplayOverrideExistingDataModal = (display: boolean) => {
        this.displayOverrideExistingDataModal = display;
    };

    setIsLoadingPDFFile = (loading: boolean) => {
        this.isLoadingPDFFile = loading;
    };

    setIsFileDeleting = (deleting: boolean) => {
        this.isFileDeleting = deleting;
    };

    setIsDraggingOverMainWindow = (dragging: boolean) => {
        this.isDraggingOverMainWindow = dragging;
    };

    setIsAiPending = (pending: boolean) => {
        this.isAiPending = pending;
    };

    setIsAiExtractionModalDismissed = (dismissed: boolean) => {
        this.isAiExtractionModalDismissed = dismissed;
    };

    toggleDisplayOverrideExistingDataModal = () => {
        this.setDisplayOverrideExistingDataModal(!this.displayOverrideExistingDataModal);
    };

    handleClickOverrideExistingDataYes = () => {
        this.setDisplayOverrideExistingDataModal(false);
        if (this.currentFileInputEvent) {
            this.handleFileInputChange({
                displayOverrideExistingDataModal: false,
                overrideData: true
            })(this.currentFileInputEvent);
        }
    };

    handleClickOverrideExistingDataNo = () => {
        this.setDisplayOverrideExistingDataModal(false);
        if (this.currentFileInputEvent) {
            this.handleFileInputChange({
                displayOverrideExistingDataModal: false,
                overrideData: false
            })(this.currentFileInputEvent);
        }
    };

    /**
     * Cancel any in-flight AI detection (and its parallel QR call) so a late response
     * cannot mutate state after the user has moved on. Used for:
     *   (a) new upload — called at the top of extractQrCodeDataAndUpdateInvoiceStore
     *   (b) file delete
     *   (c) route unmount (wired from InvoiceDetails)
     *   (e) save click while AI is still pending
     */
    abortAiDetection = () => {
        if (this.currentAbortController) {
            this.currentAbortController.abort();
            this.currentAbortController = undefined;
        }
        this.setIsAiPending(false);
    };

    handleDeleteInvoiceFile = async (file: IInvoiceFile | undefined) => {
        this.setIsFileDeleting(true);

        const invoiceStore = this.rootStore.invoiceStore;

        // Abort any in-flight AI detection so its result doesn't arrive after the
        // file has been removed and re-populate fields. Only clear the in-memory
        // aiDetection when it was set in this session (uploadMetadata present) —
        // DB-loaded detections stay in memory because they're still valid server-side
        // and would just reappear on the next reload.
        this.abortAiDetection();
        if (invoiceStore.currentInvoice?.aiDetection?.uploadMetadata) {
            invoiceStore.currentInvoice.clearAiDetection();
        }

        if (file && file.fileId && file.state === INVOICE_FILE_STATE.LOADED_FROM_DB) {
            await invoiceStore.currentInvoice?.deleteFile(file.fileId);
        }

        const invoiceFile: IInvoiceFile = {
            state: INVOICE_FILE_STATE.NEW
        };

        invoiceStore.currentInvoice?.updateProperty(INVOICE_PROPERTY.INVOICE_FILE, invoiceFile);

        this.setIsFileDeleting(false);
    };

    /**
     * Apply the AI detection response after both QR mapping and PDF rendering have
     * already completed. Stores the detection on the current invoice and, for
     * `outcome=match`, auto-applies the top candidate to the realestate form field
     * (only if the candidate's entityid resolves to a row in the loaded master data —
     * defensive against stale ids).
     */
    applyAiResult = async (
        aiResult: IAiDetectWithFileResponse["data"],
        qrResult: IQRExtractInvoiceData | undefined,
        overrideData: boolean
    ) => {
        const invoiceStore = this.rootStore.invoiceStore;
        const realestateSearchStore = this.rootStore.realestateSearchStore;
        const creditorSearchStore = this.rootStore.creditorSearchStore;
        const currentInvoice = invoiceStore.currentInvoice;

        if (!currentInvoice) {
            return;
        }

        // Fresh AI run: drop any "Mit KI erkannt" flags left over from a prior
        // upload before the mapper decides which fields it fills this time.
        currentInvoice.clearAiFilledFields();

        currentInvoice.setAiDetection({
            state: "completed",
            results: aiResult.results,
            uploadMetadata: {
                fileid: aiResult.fileid,
                filename: aiResult.filename,
                extension: aiResult.extension,
                mimetype: aiResult.mimetype,
                hash: aiResult.hash
            }
        });

        const realestateDetection = aiResult.results?.realestateid;
        if (
            realestateDetection &&
            realestateDetection.outcome === "match" &&
            realestateDetection.candidates.length > 0
        ) {
            const topCandidate = realestateDetection.candidates[0];
            const matchedRealestateData = realestateSearchStore.realestateData.find(
                (realestate) => realestate.id === topCandidate.entityid
            );

            if (matchedRealestateData) {
                const realestate: IRealestate = {
                    id: matchedRealestateData.id,
                    number: matchedRealestateData.number,
                    name: matchedRealestateData.name,
                    zip: matchedRealestateData.zip,
                    city: matchedRealestateData.city,
                    consolidation: matchedRealestateData.consolidation,
                    consolidationId: matchedRealestateData.consolidationId
                };
                invoiceStore.updateInvoiceAndFormWithSelectedRealestate(realestate);
                currentInvoice.setRealestateAutoAppliedByAi(true);
                // updateInvoiceAndFormWithSelectedRealestate routes through
                // Invoice.updateProperty which clears aiFilledFields for the
                // key on any write, so flag AFTER the update, not before.
                currentInvoice.setAiFilledField(INVOICE_PROPERTY.REALESTATE, true);
            }
        }

        // Creditor auto-apply — when the QR step did not resolve a creditor, or resolved
        // it only by a name/address lookup (a QR creditor found by its IBAN stays
        // authoritative, #26422). Mirrors the realestate branch but resolves the candidate
        // via a by-ids backend query, since the creditor master data may be
        // backend-searched rather than loaded.
        const creditorDetection = aiResult.results?.creditorid;
        if (
            creditorDetection &&
            creditorDetection.outcome === "match" &&
            creditorDetection.candidates.length > 0 &&
            canAiApplyCreditor(currentInvoice.creditor?.id, qrResult)
        ) {
            const topCreditorCandidate = creditorDetection.candidates[0];
            const [matchedCreditor] = await creditorSearchStore.getCreditorDataByIds([topCreditorCandidate.entityid]);

            // Re-check after the await: an IBAN-resolved QR creditor or a user choice must still win.
            if (matchedCreditor && canAiApplyCreditor(currentInvoice.creditor?.id, qrResult)) {
                await invoiceStore.updateInvoiceStoreWithSelectedCreditor(matchedCreditor);
                currentInvoice.setCreditorAutoAppliedByAi(true);
                // updateInvoiceStoreWithSelectedCreditor routes through
                // Invoice.updateProperty which clears aiFilledFields for the
                // key on any write, so flag AFTER the update, not before.
                currentInvoice.setAiFilledField(INVOICE_PROPERTY.CREDITOR, true);
            }
        }

        // Fill the QR-optional fields (amount, date, dueDate, invoiceNumber) from the
        // AI document, but only where the QR code didn't provide them. QR stays
        // authoritative; this runs after the QR mapping so it never clobbers QR values.
        if (aiResult.document?.details) {
            invoiceStore.mapAiDocumentFallbackOnCurrentInvoice(aiResult.document.details, qrResult, overrideData);
        }

        // All extraction data (QR + AI) is now present -- run the duplicate check once.
        if (currentInvoice.creditor?.id && currentInvoice.invoiceNumber && currentInvoice.duplicates?.length === 0) {
            void currentInvoice.getDuplicatesAndDisplayModal();
        }
    };

    extractQrCodeDataAndUpdateInvoiceStore = async (file: File, overrideData: boolean) => {
        const uiStore = this.rootStore.uiStore;
        const invoiceStore = this.rootStore.invoiceStore;

        const isPDFFile = dataTypes.includes(file.type);
        if (!isPDFFile) {
            uiStore.printStatusMessage(
                i18next.t("screens.kredi_flow.form.invoice.preview.file_selection_error"),
                MessageType.ERROR
            );
            return;
        }

        // Scenario (a): if a previous upload is still in flight, abort it before
        // starting a new one. Otherwise the older response could overwrite the new file's data.
        this.abortAiDetection();
        const controller = new AbortController();
        this.currentAbortController = controller;
        const { signal } = controller;

        const fileUrl = URL.createObjectURL(file);

        const invoiceFile: IInvoiceFile = {
            fileId: uuidv4(),
            invoiceFileId: uuidv4(),
            name: file.name,
            url: fileUrl,
            type: INVOICE_FILE_TYPE.INVOICE,
            mimetype: file.type,
            extension: file.name.split(".").pop(),
            fileDate: Ticket.formatDateForImage(new Date(file.lastModified)),
            state: INVOICE_FILE_STATE.HAS_CHANGED
        };

        const reader = new FileReader();
        const readFileAsBase64 = (file: File) => {
            return new Promise<string>((resolve, reject) => {
                reader.readAsDataURL(file);
                reader.onload = () => {
                    const fileBase64String = reader.result as string;
                    const base64String = fileBase64String.split(",")[1];
                    resolve(base64String);
                };
                reader.onerror = (error) => {
                    reject(error);
                };
            });
        };

        try {
            const base64String = await readFileAsBase64(file);
            if (signal.aborted) return;
            invoiceFile.base64String = base64String;

            // Fire QR + AI in parallel. QR blocks the UI (it's fast); AI runs in the
            // background and updates the realestate field when it returns. The AI call
            // is gated on the customer feature flag — when off, customers get the
            // pre-AI behaviour with QR-only extraction.
            const aiEnabled = this.rootStore.navStore.hasFeatureKrediFlowAiExtract;
            const qrPromise = invoiceStore.extractQrCodeDataFromPDF(base64String);
            const aiPromise = aiEnabled
                ? invoiceStore.aiDetectWithFile(base64String, file, signal)
                : Promise.resolve(undefined);

            if (aiEnabled) {
                // Fresh AI run: reset the dismissed flag so the modal shows again
                // even if the user closed it during a previous upload.
                this.setIsAiExtractionModalDismissed(false);
                this.setIsAiPending(true);
            }

            let qrResult: IQRExtractInvoiceData | undefined;
            try {
                qrResult = await qrPromise;
                if (signal.aborted) return;
                if (qrResult) {
                    invoiceStore.mapExtractedDataOnCurrentInvoice(qrResult, overrideData);
                }
            } catch (error) {
                console.error("QR extraction failed:", error);
            }

            invoiceStore.currentInvoice?.updateProperty(INVOICE_PROPERTY.INVOICE_FILE, invoiceFile);

            // Attach the AI handler AFTER QR mapping has run. This guarantees that AI's
            // realestate auto-apply (if outcome=match) wins over any realestate value
            // QR may have written, regardless of which promise resolved first.
            aiPromise
                .then((aiResult) => {
                    if (signal.aborted || !aiResult) return;
                    return this.applyAiResult(aiResult, qrResult, overrideData);
                })
                .catch((error) => {
                    if (error?.name === "AbortError" || error?.name === "CanceledError") return;
                    console.error("AI detection handler failed:", error);
                })
                .finally(() => {
                    if (!signal.aborted) {
                        this.setIsAiPending(false);
                    }
                });
        } catch (error) {
            console.error("Error reading file:", error);
            this.setIsAiPending(false);
        }
    };

    handleFileInputChange =
        (overrideDataVariables: { displayOverrideExistingDataModal: boolean; overrideData: boolean }) =>
        async (event: FileHTMLInputEvent) => {
            const uiStore = this.rootStore.uiStore;

            uiStore.setIsResizeObserverDisabled(false);

            if (overrideDataVariables.displayOverrideExistingDataModal) {
                this.setCurrentFileInputEvent(event);
                this.setDisplayOverrideExistingDataModal(true);
            } else {
                this.setIsLoadingPDFFile(true);

                if (event?.target?.files) {
                    const files = Array.from(event.target.files);

                    if (files.length > 0) {
                        const file: File = files[0];
                        await this.extractQrCodeDataAndUpdateInvoiceStore(file, overrideDataVariables.overrideData);
                    }

                    this.setIsLoadingPDFFile(false);
                }
            }
        };

    downloadFile = async (fileId: string) => {
        const downloadedFile = await this.rootStore.invoiceStore.downloadInvoiceFile(fileId);

        if (downloadedFile) {
            try {
                let fileBlob = await downloadedFile.data;
                const objUrl = window.URL.createObjectURL(fileBlob);

                const pdfFileInfo = await this.rootStore.splitPdfStore.getPdfInfo(fileId);

                // Trigger the download
                const a = document.createElement("a");
                a.href = objUrl;
                a.download = pdfFileInfo
                    ? pdfFileInfo.name
                    : i18next.t("screens.kredi_flow.form.invoice.preview.file_download_default_name");
                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (error) {
                console.error("Error downloading file:", error);
                this.rootStore.uiStore.printStatusMessage(
                    i18next.t("screens.kredi_flow.form.invoice.preview.file_download_error"),
                    MessageType.ERROR
                );
            }
        }
    };
}
